if (!customElements.get('localization-form')) {
  customElements.define(
    'localization-form',
    class LocalizationForm extends HTMLElement {
      constructor() {
        super();
        this.desktopMql = window.matchMedia('(min-width: 750px)');
        this.mobileMql = window.matchMedia('(max-width: 749px)');
        this.headerLayoutMql = window.matchMedia('(min-width: 990px)');
        this.header = this.closest('.header-wrapper');
        this.headerSection = this.closest('.section-header');
        this.menuDrawer = this.closest('.menu-drawer');
        this.elements = {
          input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
          button: this.querySelector('button.localization-form__select'),
          panel: this.querySelector('.disclosure__list-wrapper'),
          search: this.querySelector('input[name="country_filter"]'),
          closeButton: this.querySelector('.country-selector__close-button'),
          resetButton: this.querySelector('.country-filter__reset-button'),
          searchIcon: this.querySelector('.country-filter__search-icon'),
          liveRegion: this.querySelector('[data-country-search-results]'),
          overlay: this.querySelector('.country-selector__overlay'),
        };

        this.onContainerKeyDownEvent = this.onContainerKeyDown.bind(this);
        this.onFocusOutEvent = this.onFocusOut.bind(this);
        this.onButtonClickEvent = this.onButtonClick.bind(this);
        this.onDocumentClickEvent = this.onDocumentClick.bind(this);
        this.onPageShowEvent = this.onPageShow.bind(this);
        this.onLayoutChangeEvent = this.onLayoutChange.bind(this);

        this.addEventListener('keydown', this.onContainerKeyDownEvent);
        this.addEventListener('focusout', this.onFocusOutEvent);
        this.elements.button.addEventListener('click', this.onButtonClickEvent);

        if (this.elements.search) {
          this.elements.search.addEventListener('keyup', this.filterCountries.bind(this));
          this.elements.search.addEventListener('focus', this.onSearchFocus.bind(this));
          this.elements.search.addEventListener('blur', this.onSearchBlur.bind(this));
          this.elements.search.addEventListener('keydown', this.onSearchKeyDown.bind(this));
        }
        if (this.elements.closeButton) {
          this.elements.closeButton.addEventListener('click', () => this.hidePanel({ restoreFocus: true }));
        }
        if (this.elements.resetButton) {
          this.elements.resetButton.addEventListener('click', this.resetFilter.bind(this));
          this.elements.resetButton.addEventListener('mousedown', (event) => event.preventDefault());
        }
        if (this.elements.overlay) {
          this.elements.overlay.addEventListener('click', () => this.hidePanel({ restoreFocus: true }));
        }

        this.querySelectorAll('a').forEach((item) => item.addEventListener('click', this.onItemClick.bind(this)));
      }

      connectedCallback() {
        window.addEventListener('pageshow', this.onPageShowEvent);
        this.mobileMql.addEventListener('change', this.onLayoutChangeEvent);
        this.headerLayoutMql.addEventListener('change', this.onLayoutChangeEvent);
      }

      disconnectedCallback() {
        this.hidePanel({ restoreFocus: false });
        window.removeEventListener('pageshow', this.onPageShowEvent);
        this.mobileMql.removeEventListener('change', this.onLayoutChangeEvent);
        this.headerLayoutMql.removeEventListener('change', this.onLayoutChangeEvent);
      }

      get isOpen() {
        return !this.elements.panel.hasAttribute('hidden');
      }

      get isMobileCountrySelector() {
        return this.mobileMql.matches && this.elements.panel.classList.contains('country-selector');
      }

      onButtonClick(event) {
        event.preventDefault();

        if (this.isOpen) {
          this.hidePanel({ restoreFocus: true });
        } else {
          this.showPanel();
        }
      }

      showPanel() {
        this.closeOtherSelectors();
        this.closeHeaderSearch();

        this.elements.panel.removeAttribute('hidden');
        this.elements.button.setAttribute('aria-expanded', 'true');
        this.setHeaderLocalizationOpen(true);
        this.syncResponsiveState();
        document.addEventListener('click', this.onDocumentClickEvent);

        if (this.elements.search && this.desktopMql.matches) {
          this.elements.search.focus();
        } else {
          this.elements.button.focus();
        }
      }

      hidePanel({ restoreFocus = false } = {}) {
        this.elements.button.setAttribute('aria-expanded', 'false');
        this.elements.panel.setAttribute('hidden', '');
        document.removeEventListener('click', this.onDocumentClickEvent);

        if (this.elements.search) {
          this.elements.search.value = '';
          this.filterCountries();
          this.elements.search.removeAttribute('aria-activedescendant');
        }

        this.syncResponsiveState();
        this.setHeaderLocalizationOpen(false);

        if (restoreFocus && this.elements.button.isConnected) {
          this.elements.button.focus();
        }
      }

      closeOtherSelectors() {
        document.querySelectorAll('localization-form').forEach((form) => {
          if (form !== this && form.isOpen) form.hidePanel({ restoreFocus: false });
        });
      }

      closeHeaderSearch() {
        if (!this.headerSection) return;

        this.headerSection.querySelectorAll('details-modal.header__search').forEach((searchModal) => {
          if (searchModal.isOpen?.()) searchModal.close(false);
        });
      }

      setHeaderLocalizationOpen(isOpen) {
        if (!this.headerSection || !this.header) return;

        const anotherSelectorIsOpen = Array.from(
          this.headerSection.querySelectorAll('localization-form')
        ).some((form) => form !== this && form.isOpen);

        if (!isOpen && anotherSelectorIsOpen) return;

        if (typeof this.header.setLocalizationOpen === 'function') {
          this.header.setLocalizationOpen(isOpen);
          return;
        }

        this.headerSection.classList.toggle('localization-open', isOpen);
        if (isOpen) {
          this.headerSection.classList.add('shopify-section-header-sticky');
          this.headerSection.classList.remove('shopify-section-header-hidden', 'animate');
        } else {
          this.headerSection.classList.remove('shopify-section-header-sticky', 'animate');
        }
      }

      syncResponsiveState() {
        const shouldLock = this.isOpen && this.isMobileCountrySelector;

        if (this.menuDrawer) {
          this.menuDrawer.classList.toggle('country-selector-open', shouldLock);
        }

        const anotherMobileCountrySelectorIsOpen = Array.from(document.querySelectorAll('localization-form')).some(
          (form) => form !== this && form.isOpen && form.isMobileCountrySelector
        );
        document.body.classList.toggle('overflow-hidden-mobile', shouldLock || anotherMobileCountrySelectorIsOpen);
      }

      onDocumentClick(event) {
        if (!this.isOpen || this.contains(event.target)) return;
        this.hidePanel({ restoreFocus: this.contains(document.activeElement) });
      }

      onFocusOut(event) {
        if (!this.isOpen || !event.relatedTarget || this.contains(event.relatedTarget)) return;
        this.hidePanel({ restoreFocus: false });
      }

      onLayoutChange() {
        if (this.isOpen) this.hidePanel({ restoreFocus: false });
      }

      onPageShow() {
        this.hidePanel({ restoreFocus: false });
      }

      onContainerKeyDown(event) {
        if (!this.isOpen) return;

        if (event.code.toUpperCase() === 'ESCAPE') {
          event.preventDefault();
          event.stopPropagation();
          this.hidePanel({ restoreFocus: true });
          return;
        }

        if (!['ARROWUP', 'ARROWDOWN'].includes(event.code.toUpperCase())) return;

        const focusableItems = Array.from(this.querySelectorAll('a')).filter(
          (item) => !item.parentElement.classList.contains('hidden')
        );
        if (!focusableItems.length) return;

        event.preventDefault();
        const focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
        const itemToFocus =
          event.code.toUpperCase() === 'ARROWUP'
            ? focusableItems[focusedItemIndex > 0 ? focusedItemIndex - 1 : focusableItems.length - 1]
            : focusableItems[focusedItemIndex < focusableItems.length - 1 ? focusedItemIndex + 1 : 0];

        itemToFocus.focus();
        if (this.elements.search) this.elements.search.setAttribute('aria-activedescendant', itemToFocus.id);
      }

      onItemClick(event) {
        event.preventDefault();
        const form = this.querySelector('form');
        this.elements.input.value = event.currentTarget.dataset.value;
        this.hidePanel({ restoreFocus: false });
        if (form) form.submit();
      }

      normalizeString(str) {
        return str
          .normalize('NFD')
          .replace(/\p{Diacritic}/gu, '')
          .toLowerCase();
      }

      filterCountries() {
        if (!this.elements.search) return;

        const searchValue = this.normalizeString(this.elements.search.value);
        const popularCountries = this.querySelector('.popular-countries');
        const allCountries = this.querySelectorAll('a');
        let visibleCountries = 0;

        this.elements.resetButton.classList.toggle('hidden', !searchValue);

        if (popularCountries) {
          popularCountries.classList.toggle('hidden', Boolean(searchValue));
        }

        allCountries.forEach((item) => {
          const countryName = this.normalizeString(item.querySelector('.country').textContent);
          const isVisible = countryName.includes(searchValue);
          item.parentElement.classList.toggle('hidden', !isVisible);
          if (isVisible) visibleCountries++;
        });

        if (this.elements.liveRegion) {
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.countrySelectorSearchCount.replace(
            '[count]',
            visibleCountries
          );
        }

        this.elements.panel.scrollTop = 0;
        this.querySelector('.country-selector__list').scrollTop = 0;
      }

      resetFilter(event) {
        event.stopPropagation();
        this.elements.search.value = '';
        this.filterCountries();
        this.elements.search.focus();
      }

      onSearchFocus() {
        this.elements.searchIcon.classList.add('country-filter__search-icon--hidden');
      }

      onSearchBlur() {
        if (!this.elements.search.value) {
          this.elements.searchIcon.classList.remove('country-filter__search-icon--hidden');
        }
      }

      onSearchKeyDown(event) {
        if (event.code.toUpperCase() === 'ENTER') {
          event.preventDefault();
        }
      }
    }
  );
}
